<template>
  <div class="model-manager">
    <a-card>
      <div class="search-bar">
        <a-form layout="inline">
          <a-form-item label="功能类型">
            <a-input v-model:value="searchParams.type" style="width: 180px" placeholder="请输入功能类型" />
          </a-form-item>
          <a-form-item label="标识符">
            <a-input v-model:value="searchParams.identifier" style="width: 180px" placeholder="请输入标识符" />
          </a-form-item>
          <a-form-item>
            <a-button type="primary" @click="handleSearch">查询</a-button>
          </a-form-item>
          <a-form-item>
            <a-button @click="resetSearch">重置</a-button>
          </a-form-item>
        </a-form>
      </div>

      <div class="table-bar">
        <a-button type="primary" @click="showAddModel">新增</a-button>
        <div class="table-tools">
          <a-button type="text">
            <font-awesome-icon icon="fa-solid fa-rotate-right" />
          </a-button>
          <a-button type="text">
            <font-awesome-icon icon="fa-solid fa-table-cells-large" />
          </a-button>
          <a-button type="text">
            <font-awesome-icon icon="fa-solid fa-gear" />
          </a-button>
        </div>
      </div>

      <a-table :columns="columns" :data-source="filteredData" :pagination="false" style="margin-top: 16px;">
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'action'">
            <a-button type="link" size="small" @click="showEditModel(record)">编辑</a-button>
            <a-divider type="vertical" />
            <a-popconfirm title="确定删除吗?" ok-text="确定" cancel-text="取消" @confirm="deleteModel(record.key)">
              <a-button type="link" size="small" danger>删除</a-button>
            </a-popconfirm>
          </template>
        </template>
        <template #emptyText>
          <a-empty description="暂无数据" />
        </template>
      </a-table>
    </a-card>

    <a-modal
      v-model:open="isModalVisible"
      :title="isEditing ? '编辑模型' : '新增模型'"
      ok-text="确定"
      cancel-text="取消"
      @ok="handleOk"
    >
      <a-form :model="currentModel" layout="vertical">
        <a-form-item label="功能类型" name="type" :rules="[{ required: true, message: '请输入功能类型!' }]">
          <a-input v-model:value="currentModel.type" />
        </a-form-item>
        <a-form-item label="功能名称" name="name" :rules="[{ required: true, message: '请输入功能名称!' }]">
          <a-input v-model:value="currentModel.name" />
        </a-form-item>
        <a-form-item label="标识符" name="identifier">
          <a-input v-model:value="currentModel.identifier" />
        </a-form-item>
        <a-form-item label="数据类型" name="dataType">
          <a-input v-model:value="currentModel.dataType" />
        </a-form-item>
        <a-form-item label="读写类型" name="accessType">
          <a-input v-model:value="currentModel.accessType" />
        </a-form-item>
        <a-form-item label="数据定义" name="definition">
          <a-textarea v-model:value="currentModel.definition" />
        </a-form-item>
        <a-form-item label="上传插件" name="pluginFile">
          <a-upload
            v-model:file-list="currentModel.pluginFile"
            name="file"
            action="/upload/dummy" 
            :before-upload="() => false" 
          >
            <a-button>
              <font-awesome-icon icon="fa-solid fa-upload" /> 点击上传
            </a-button>
          </a-upload>
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed } from 'vue';
import { modelDataList, toCamelCase } from '@/data/modelData';
import { FormInstance, message } from 'ant-design-vue';

interface ModelItem {
  key: string;
  type: string;
  name: string;
  identifier: string;
  dataType: string;
  accessType: string;
  definition: string;
  pluginFile?: any; // 新增字段，? 表示可选，具体类型根据上传组件的返回值确定
}

const columns = [
  { title: '功能类型', dataIndex: 'type', key: 'type' },
  { title: '功能名称', dataIndex: 'name', key: 'name' },
  { title: '标识符', dataIndex: 'identifier', key: 'identifier' },
  { title: '数据类型', dataIndex: 'dataType', key: 'dataType' },
  { title: '读写类型', dataIndex: 'accessType', key: 'accessType' },
  { title: '数据定义', dataIndex: 'definition', key: 'definition' },
  // 可以考虑添加一列显示插件信息，暂时不加
  { title: '操作', key: 'action' }
];

const modelData = ref<ModelItem[]>(JSON.parse(JSON.stringify(modelDataList))); // Deep copy

const isModalVisible = ref(false);
const isEditing = ref(false);
const currentModel = reactive<Partial<ModelItem>>({});
let editingKey = '';

const searchParams = reactive({
  type: '',
  identifier: ''
});

const filteredData = computed(() => {
  return modelData.value.filter(item => {
    const typeMatch = item.type.toLowerCase().includes(searchParams.type.toLowerCase());
    const identifierMatch = item.identifier.toLowerCase().includes(searchParams.identifier.toLowerCase());
    return typeMatch && identifierMatch;
  });
});

const handleSearch = () => {
  // Computed property handles filtering, this function can be used for any additional logic if needed
  console.log('Searching with:', searchParams);
};

const resetSearch = () => {
  searchParams.type = '';
  searchParams.identifier = '';
};

const showAddModel = () => {
  isEditing.value = false;
  Object.assign(currentModel, {
    type: '',
    name: '',
    identifier: '',
    dataType: '结构化数据',
    accessType: '读写',
    definition: '',
    pluginFile: [] // 初始化为空数组，用于 a-upload
  });
  isModalVisible.value = true;
};

const showEditModel = (record: ModelItem) => {
  isEditing.value = true;
  editingKey = record.key;
  Object.assign(currentModel, { ...record });
  isModalVisible.value = true;
};

const handleOk = async () => {
  if (!currentModel.name || !currentModel.type) {
    message.error('功能类型和功能名称不能为空！');
    return;
  }
  if (!currentModel.identifier) {
    currentModel.identifier = toCamelCase(currentModel.name || '');
  }

  if (isEditing.value) {
    const index = modelData.value.findIndex(item => item.key === editingKey);
    if (index > -1) {
      modelData.value[index] = { ...currentModel, key: editingKey } as ModelItem;
      message.success('编辑成功');
    }
  } else {
    const newKey = `model-${Date.now()}`;
    modelData.value.push({ ...currentModel, key: newKey } as ModelItem);
    message.success('新增成功');
  }
  isModalVisible.value = false;
};

const deleteModel = (key: string) => {
  modelData.value = modelData.value.filter(item => item.key !== key);
  message.success('删除成功');
};

</script>

<style lang="less" scoped>
.model-manager {
  padding: 0 0 24px 0;
  .search-bar {
    margin-bottom: 16px;
    background: #fafbfc;
    padding: 16px 16px 0 16px;
    border-radius: 4px;
  }
  .table-bar {
    display: flex;
    align-items: center;
    margin-bottom: 8px;
    .table-tools {
      margin-left: auto;
      display: flex;
      gap: 8px;
    }
  }
}
</style>